import React, { useEffect, useState, useMemo, useRef } from "react";
import { Container, Card } from "react-bootstrap";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getAdminReport,
  getAdminReportEmployees,
  getAdminReportModules,
} from "../../api/adminReports";

import ReportFilters from "./components/ReportFilters/page";
import ReportStatsCards from "./components/ReportStatsCards/page";
import ReportToolbar from "./components/ReportToolbar/page";
import ReportTable from "./components/ReportTable/page";

const PAGE_SIZE = 16;

const DEFAULT_STATS = {
  workingDays: 0,
  totalHours: 0,
  tasksCompleted: 0,
  modulesWorkedOn: 0,
};

// ADD THIS:
const getTodayDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Converts an API date/datetime value to a local "YYYY-MM-DD" string
// (same local-date logic the table uses to display Reported Date).
const toLocalYMD = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// jsPDF's built-in fonts only support Latin-1. Convert common special
// characters to safe equivalents and drop anything else, so a line never
// switches encoding (which causes the stretched/garbled text).
const pdfSafe = (value) =>
  String(value ?? "")
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201F\u2033]/g, '"')
    .replace(/[\u2013\u2014\u2212]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2192\u21D2\u279C\u27A1]/g, "->")
    .replace(/[\u2190\u21D0]/g, "<-")
    .replace(/[\u2022\u25CF\u25AA\u2023]/g, "-")
    .replace(/[\u2713\u2714]/g, "v")
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, "")
    .replace(/[^\x09\x0A\x20-\x7E\xA0-\xFF]/g, "");

const AdminReport = () => {
  const token = localStorage.getItem("token");
  const requestIdRef = useRef(0);

  const [employeeUsers, setEmployeeUsers] = useState([]); // { id, name }
  const [modules, setModules] = useState([]); // { id, name }
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [fromDate, setFromDate] = useState(getTodayDate());
const [toDate, setToDate] = useState(getTodayDate());
  const [groupBy, setGroupBy] = useState(""); // "" | "working" | "reported"
    const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [errors, setErrors] = useState({ date: "" });
  const [resetTrigger, setResetTrigger] = useState(0);
  const [reports, setReports] = useState([]); // rows straight from the API
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [exporting, setExporting] = useState(null); // "pdf" | "excel" | null

  // Fetch dropdown data — employees & modules now come from the same
  // admin-report backend so their ids line up exactly with what the
  // report query expects.
  useEffect(() => {
    getAdminReportEmployees(token)
      .then((res) => setEmployeeUsers(res.data))
      .catch(() => setEmployeeUsers([]));

    getAdminReportModules(token)
      .then((res) => setModules(res.data))
      .catch(() => setModules([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateForm = () => {
    // Employee is no longer required — "All Employees" is a real, working
    // option now that the backend treats a missing employeeId as "no filter".
    if (!fromDate) {
      setErrors({ date: "From Date is required" });
      return false;
    }
    setErrors({ date: "" });
    return true;
  };

  const fetchReports = () => {
  if (!fromDate) {
    setReports([]);
    setStats(DEFAULT_STATS);
    return;
  }

  const thisRequestId = ++requestIdRef.current;

  getAdminReport(token, {
    fromDate,
    toDate: toDate || fromDate,
    employeeId: selectedUser || undefined,
    moduleId: selectedModule || undefined,
    dateType: groupBy === "reported" ? "reported" : "working",
  })
  
    .then((res) => {
      if (thisRequestId !== requestIdRef.current) return; // stale response, ignore
      const rows = res.data.rows || [];
      console.log("ADMIN ROW SAMPLE:", rows[0]);
      setReports(rows);
      setStats(res.data.stats || DEFAULT_STATS);
    })
    .catch(() => {
      if (thisRequestId !== requestIdRef.current) return; // stale response, ignore
      setReports([]);
      setStats(DEFAULT_STATS);
      toast.error("Failed to load report");
    });
};

const flatRows = useMemo(() => {
    const mappedRows = reports.map((r) => ({
      id: r.id,
      workingDate: r.date,
      reportedDate: r.reportedDate,
      moduleName: r.module,
      workNatureName: r.nature,
      workDetails: r.details,
      hours: r.hours,
      clientName: r.client || r.clientName || "-",
      workingFrom: r.workingFrom || "-",
      employeeName: r.employeeName || "Unknown Employee",
    }));

    // Default ("All Date Type") sorts by Working Date; earliest date first (1/9 then 2/9 ...)
    const key = groupBy === "reported" ? "reportedDate" : "workingDate";
    return [...mappedRows].sort((a, b) => {
      const ad = toLocalYMD(a[key]);
      const bd = toLocalYMD(b[key]);
      if (ad === bd) return 0;
      if (!ad) return 1; // rows with no date go last
      if (!bd) return -1;
      return ad < bd ? -1 : 1;
    });
  }, [reports, groupBy]);
  const searchedRows = useMemo(() => {
    if (!search.trim()) return flatRows;
    const s = search.trim().toLowerCase();

    // true if every letter of `q` appears in `text` in order (handles typos like "desin" -> "design")
    const looseMatch = (text, q) => {
      let i = 0;
      for (const ch of text) {
        if (ch === q[i]) i++;
        if (i === q.length) return true;
      }
      return false;
    };

    // If the text matches a work nature (full, partial or loosely typed),
    // filter by work nature only, so words inside work details don't add wrong rows.
    const natureMatches = (r) => {
      const n = (r.workNatureName || "").toLowerCase();
      return n.includes(s) || (s.length >= 3 && looseMatch(n, s));
    };
    if (flatRows.some(natureMatches)) {
      return flatRows.filter(natureMatches);
    }

    return flatRows.filter(
      (r) =>
        (r.employeeName || "").toLowerCase().includes(s) ||
        (r.moduleName || "").toLowerCase().includes(s) ||
        (r.workNatureName || "").toLowerCase().includes(s) ||
        (r.workDetails || "").toLowerCase().includes(s)
    );
  }, [flatRows, search]);

  // One table row per employee + working date; the individual entries
  // are kept inside `entries` so the View popup can list all of them.
  const groupedRows = useMemo(() => {
    const map = new Map();
    searchedRows.forEach((r) => {
      const key = `${r.employeeName}__${toLocalYMD(r.workingDate)}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          employeeName: r.employeeName,
          workingDate: r.workingDate,
          reportedDate: r.reportedDate,
          hours: 0,
          entries: [],
        });
      }
      const g = map.get(key);
      g.entries.push(r);
      g.hours += Number(r.hours) || 0;
      // keep the latest reported date of the day
      if (r.reportedDate && (!g.reportedDate || new Date(r.reportedDate) > new Date(g.reportedDate))) {
        g.reportedDate = r.reportedDate;
      }
    });
    return Array.from(map.values());
  }, [searchedRows]);

  const totalPages = Math.max(1, Math.ceil(groupedRows.length / PAGE_SIZE));
  const paginatedRows = groupedRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleReset = () => {
  setFromDate(getTodayDate());
  setToDate(getTodayDate());
    setSelectedUser("");
    setSelectedModule("");
    setGroupBy("");
    setSearch("");
    setPage(1);
    setReports([]);
    setStats(DEFAULT_STATS);
    setErrors({ date: "" });
    setResetTrigger((n) => n + 1);
  };

 useEffect(() => {
    if (!fromDate) {
      setReports([]);
      setStats(DEFAULT_STATS);
      return;
    }
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedUser, selectedModule, groupBy, resetTrigger]);
    // ── Real PDF export (jsPDF + autotable), respects search + filters ──
  const EXPORT_COLUMNS = ["Date", "Project Module", "Work Nature", "Work Details", "Hours"];
  const toExportRow = (r) => [
    r.workingDate,
    r.moduleName,
    r.workNatureName,
    r.workDetails,
    `${r.hours}h`,
  ];

 const handleExportPdf = () => {
    if (!validateForm()) return;
    if (!searchedRows.length) {
      toast.error("No Data found");
      return;
    }
    setExporting("pdf");
    try {
      // Group the flat, filtered rows by EMPLOYEE — each employee gets one
      // section listing all their entries across the selected date range.
      const groupsMap = new Map();
      searchedRows.forEach((r) => {
        const key = r.employeeName;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, { employeeName: r.employeeName, entries: [], totalHours: 0 });
        }
        const g = groupsMap.get(key);
        g.entries.push(r);
        g.totalHours += Number(r.hours) || 0;
      });
      const employeeGroups = Array.from(groupsMap.values()).sort((a, b) =>
        a.employeeName.localeCompare(b.employeeName)
      );

      const doc = new jsPDF();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;

      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.text(
        `Admin Reports — ${fromDate} to ${toDate || "present"}`,
        margin,
        18
      );
      let cursorY = 26;

      employeeGroups.forEach((group) => {
        if (cursorY > pageHeight - 60) {
          doc.addPage();
          cursorY = 20;
        }

        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text(pdfSafe(group.employeeName), margin, cursorY);
        doc.setFont(undefined, "normal");
        doc.setFontSize(10);
        doc.setTextColor(107, 114, 128);
        doc.text(
          `Total: ${group.totalHours}h  •  ${group.entries.length} ${
            group.entries.length === 1 ? "entry" : "entries"
          }`,
          pageWidth - margin,
          cursorY,
          { align: "right" }
        );
        doc.setTextColor(0, 0, 0);



                const fmtDate = (v) =>
          v
            ? new Date(v).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-";

        // keep entries in order, newest day first
        const sortedEntries = [...group.entries].sort((a, b) => {
          const ad = toLocalYMD(a.workingDate);
          const bd = toLocalYMD(b.workingDate);
          return ad < bd ? 1 : ad > bd ? -1 : 0;
        });

        // totals per day
        const dayTotals = {};
        sortedEntries.forEach((e) => {
          const k = toLocalYMD(e.workingDate);
          if (!dayTotals[k]) dayTotals[k] = { hours: 0, count: 0 };
          dayTotals[k].hours += Number(e.hours) || 0;
          dayTotals[k].count += 1;
        });

        const bottomLimit = pageHeight - 15;
        let y = cursorY + 6;
        let lastDay = null;

        sortedEntries.forEach((e) => {
          const day = toLocalYMD(e.workingDate);
          const details = pdfSafe(e.workDetails || "-")
            .replace(/\r/g, "")
            .replace(/\n{3,}/g, "\n\n")
            .trim();

          // start a new page if the entry header + a few lines won't fit
          doc.setFontSize(9);
          const lineCount = doc.splitTextToSize(details, pageWidth - margin * 2 - 12).length;
          const needDateBar = day !== lastDay;
          const needed = 30 + Math.min(lineCount, 4) * 4.2 + (needDateBar ? 12 : 0);
          if (y + needed > bottomLimit) {
            doc.addPage();
            y = 20;
          }

          // blue date bar (once per day)
          if (needDateBar) {
            const t = dayTotals[day];
            doc.setFillColor(47, 95, 220);
            doc.rect(margin, y, pageWidth - margin * 2, 7.5, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, "bold");
            doc.setFontSize(10);
            doc.text(fmtDate(e.workingDate), margin + 3, y + 5.2);
            doc.text(
              `${t.count} ${t.count === 1 ? "entry" : "entries"}  •  Total ${t.hours}h`,
              pageWidth - margin - 3,
              y + 5.2,
              { align: "right" }
            );
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, "normal");
            y += 9;
            lastDay = day;
          }

          // entry card: labelled summary row + full-width task box
          autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [["Client", "Module", "Work Nature", "From", "Hours"]],
            body: [
              [
                pdfSafe(e.clientName || "-"),
                pdfSafe(e.moduleName || "-"),
                pdfSafe(e.workNatureName || "-"),
                pdfSafe(e.workingFrom || "-"),
                `${e.hours}h`,
              ],
              [
                {
                  content: details,
                  colSpan: 5,
                  styles: {
                    fillColor: [255, 255, 255],
                    textColor: [55, 65, 81],
                    fontStyle: "normal",
                    cellPadding: { top: 5, right: 6, bottom: 6, left: 6 },
                  },
                },
              ],
            ],
            showHead: "firstPage",
            theme: "grid",
            styles: {
              fontSize: 9,
              cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
              overflow: "linebreak",
              valign: "top",
              lineColor: [229, 231, 235],
              lineWidth: 0.2,
            },
            headStyles: {
              fillColor: [243, 244, 246],
              textColor: [109, 40, 217],
              fontStyle: "bold",
              fontSize: 7.5,
            },
            columnStyles: {
              0: { cellWidth: 38 },
              1: { cellWidth: "auto" },
              2: { cellWidth: 34 },
              3: { cellWidth: 22 },
              4: { cellWidth: 20, halign: "right" },
            },
            didParseCell: (data) => {
              if (data.section === "body" && data.row.index === 0) {
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.fillColor = [237, 241, 252];
                data.cell.styles.textColor = [17, 24, 39];
              }
            },
          });

          y = doc.lastAutoTable.finalY + 6;
        });

        cursorY = doc.lastAutoTable.finalY + 14;
      });

      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 6, {
          align: "right",
        });
      }
      doc.save(`admin-report-${fromDate}-to-${toDate || "present"}.pdf`);
      toast.success("Downloaded");
    } catch (err) {
      toast.error("Export to PDF failed.");
    } finally {
      setExporting(null);
    }
  };

  // ── Real Excel export (xlsx), respects search + filters ──
  const handleExportExcel = () => {
    if (!searchedRows.length) {
      toast.error("No Data found");
      return;
    }
    setExporting("excel");
    try {
      const sheetData = [EXPORT_COLUMNS, ...searchedRows.map(toExportRow)];
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      worksheet["!cols"] = [{ wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 40 }, { wch: 8 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Admin Report");

      XLSX.writeFile(workbook, `admin-report-${fromDate}-to-${toDate || "present"}.xlsx`);
      toast.success("Downloaded");
    } catch (err) {
      toast.error("Export to Excel failed.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <Container
      fluid
      className="p-4"
      style={{
        height: "calc(100vh - 62px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <h4 className="page-main-heading mb-3">
  Admin Reports View
</h4>

      <ReportFilters
        fromDate={fromDate}
        toDate={toDate}
        selectedUser={selectedUser}
        selectedModule={selectedModule}
        groupBy={groupBy}
        employeeUsers={employeeUsers}
        modules={modules}
        errors={errors}
        onFromDateChange={(v) => {
          setFromDate(v);
          setErrors({ date: "" });
        }}
        onToDateChange={(v) => {
          if (!fromDate) {
            setErrors({ date: "From Date is mandatory" });
            return;
          }
          setToDate(v);
        }}
        onUserChange={setSelectedUser}
        onModuleChange={setSelectedModule}
        onGroupByChange={(v) => {
          setGroupBy(v);
          setPage(1);
        }}
               onReset={handleReset}
      />

      <ReportStatsCards stats={stats} reported={groupBy === "reported"} />

      <Card style={{ border: "1px solid #E5E7EB", borderRadius: 12, flex: 1, minHeight: 0 }}>
        <Card.Body style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          <ReportToolbar
            search={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            exporting={exporting}
            onExportPDF={handleExportPdf}
            onExportExcel={handleExportExcel}
          />

          <ReportTable
            paginatedRows={paginatedRows}
            searchedCount={groupedRows.length}
            page={page}
            totalPages={totalPages}
            pageSize={PAGE_SIZE}
            onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Card.Body>
      </Card>

      <ToastContainer position="top-right" autoClose={3000} />
    </Container>
  );
};

export default AdminReport;
